/* eslint-disable sonarjs/no-duplicate-string */
import { TemplateResult, html, nothing, unsafeCSS } from 'lit';
import Cookies from 'js-cookie';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { classMap } from 'lit/directives/class-map.js';
import { ContextConsumer, consume } from '@lit/context';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafInputgroup } from '@cigna/dcws_leaf-web-components/components/inputgroup/inputgroup.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafModal } from '@cigna/dcws_leaf-web-components/components/modal/modal.component.js';
import { LeafSelect } from '@cigna/dcws_leaf-web-components/components/select/select.component.js';
import { LeafSelectOption } from '@cigna/dcws_leaf-web-components/components/select-option/select-option.component.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { LeafTab } from '@cigna/dcws_leaf-web-components/components/tabs/tabs.component.js';
import { LeafTabSection } from '@cigna/dcws_leaf-web-components/components/tabs/tab-section.component.js';
import { LeafTabItem } from '@cigna/dcws_leaf-web-components/components/tabs/tab-item.component.js';
import { LeafValidationMessage } from '@cigna/dcws_leaf-web-components/components/validation-message/validation-message.component.js';
import { watch } from '@cigna/dcws_leaf-web-components/utilities/watch.js';
import { sanitize } from '@cigna/dcws_leaf-web-components/utilities/sanitize.js';
import { updateDynamicLinks } from '../campaigns/link-service.js';
import { ICampaignInfo } from '../campaigns/interfaces.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import { dynamicLinksContext, endpointsContext } from '../contexts.js';
import { zipLookup } from './utils/api/zipdata.js';
import { planResults } from './utils/api/planresults.js';
import styles from './medicare-plan-finder.scss?inline';
import type { PlanFinderData, ZipData, PlanResultsData, PlanObject } from './interfaces.js';

export class CHCMedicarePlanFinder extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-input': LeafInput,
    'leaf-inputgroup': LeafInputgroup,
    'leaf-label': LeafLabel,
    'leaf-modal': LeafModal,
    'leaf-select': LeafSelect,
    'leaf-select-option': LeafSelectOption,
    'leaf-spinner': LeafSpinner,
    'leaf-tab': LeafTab,
    'leaf-tab-section': LeafTabSection,
    'leaf-tab-item': LeafTabItem,
    'leaf-validation-message': LeafValidationMessage,
  };
  protected override ns = 'medicare-plan-finder';
  private planFinderData?: PlanFinderData;

  @property()
  variant: 'basic' | 'wizard' | 'plan-documents' = 'basic';

  @property({ type: Array })
  planYears = [2024, 2025];

  @property({ reflect: true, type: Boolean, attribute: 'hide-content' })
  hideContent = false;

  @property({ attribute: false })
  planDocZipArray: Array<string> = [];

  @property({ attribute: false })
  planDocCounties: Array<string> = [];

  @property({ attribute: false })
  planDocContracts: Array<string> = [];

  @property({ attribute: false })
  planDocZipLookupData: Array<Array<ZipData>> = [];

  @property({ attribute: false })
  planDocCountyView: Array<boolean> = [];

  @state()
  endPoint?: string;

  @state()
  igseEndPoint?: string;

  @state()
  showLoading = false;

  @state()
  showContractLoading = false;

  @state()
  showCountyView = false;

  @state()
  showContractSelect = false;

  @state()
  zipInputChanged = false;

  @state()
  zipInputSuccess = false;

  @state()
  activeYear = 2025;

  @property({ reflect: true })
  planType: 'MAPD' | 'Supp' | 'PDP' = 'MAPD';

  @property({ reflect: true })
  contract?: LeafSelect;

  @property({ reflect: true })
  county?: LeafSelect;

  @property({ reflect: true })
  zip = '';

  @property({ reflect: true })
  pid!: string;

  @property({ attribute: false })
  filteredZipData?: ZipData;

  @state()
  zipLookupData: Array<ZipData> = [];

  @state()
  planResultsData: PlanObject[] = [];

  @state()
  planDocResultsData: Array<PlanObject[]> = [];

  @state()
  zipApiFailure = false;

  @state()
  planApiFailure = false;

  @property({ attribute: false })
  planDocZipApiFailure: Array<boolean> = [];

  @state()
  planDocPlanApiFailure: Array<boolean> = [];

  @state()
  activeTabIndex = 0;

  @state()
  initialView = true;

  @consume({ context: dynamicLinksContext, subscribe: true })
  @state()
  private campaignsObj!: ICampaignInfo;

  private isPlanDoc = false;

  @watch('variant')
  setPlanDoc() {
    this.isPlanDoc = this.variant === 'plan-documents';
  }

  override onNamespacesLoaded = () => {
    this.planFinderData = this.i18n?.getLanguageData(this.ns) as PlanFinderData;
  };

  get basicCountyForm() {
    return this.shadowRoot?.querySelector('form[name="county-form"]');
  }

  constructor() {
    super();
    new ContextConsumer(this, {
      context: endpointsContext,
      callback: (endpoints) => {
        this.endPoint = endpoints.medicarePlanFinder;
        this.igseEndPoint = endpoints.igse;
      },
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('leaf:show', this.showStickyTransform);
    document.addEventListener('leaf:after-hide', this.hideStickyTransform);

    //dual year
    if (this.isPlanDoc && this.planYears.length > 1) {
      this.activeYear = this.planYears[0];
    }
  }

  override disconnectedCallback() {
    document.removeEventListener('leaf:show', this.showStickyTransform);
    document.removeEventListener('leaf:after-hide', this.hideStickyTransform);
    super.disconnectedCallback();
  }

  private showStickyTransform = (event: Event) => {
    if ((event.target as HTMLElement)?.tagName.toLowerCase() === 'chcapp-medicare-plan-finder') {
      const stickyBanner = this.closest('chc-sticky-banner');
      if (stickyBanner) {
        const stickyBg = stickyBanner.shadowRoot?.querySelector('.chc-c-sticky-banner__bg');
        stickyBg?.setAttribute('style', 'transform: unset;');
      }
    }
  };

  private hideStickyTransform = (event: Event) => {
    if ((event.target as HTMLElement)?.tagName.toLowerCase() === 'chcapp-medicare-plan-finder') {
      const stickyBanner = this.closest('chc-sticky-banner');
      if (stickyBanner) {
        const stickyBg = stickyBanner.shadowRoot?.querySelector('.chc-c-sticky-banner__bg');
        stickyBg?.removeAttribute('style');
      }
    }
  };

  attachFormSubmit() {
    if (this.variant === 'basic') {
      this.basicCountyForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const countySelect: HTMLSelectElement | null = form.querySelector('#county-select-id select');
        const locationHash = `zip=${this.zip}`;
        const countyVal = countySelect ? sanitize(countySelect.value) : undefined;
        this.filteredZipData = this.zipLookupData.find((lookup) => lookup.CountyName === countyVal);
        if (this.filteredZipData?.CountyFIPS) {
          window.location.hash = locationHash;
          const redirectUrl = this.buildDeeplink();
          window.location.assign(redirectUrl);
        }
      });
    }
  }

  setFinderProperties() {
    if (this.planYears.length > 1) {
      const selectedYear = this.shadowRoot?.querySelector('leaf-tab-section[active]')?.getAttribute('label');
      this.activeYear = parseInt(selectedYear || '');
    }
  }

  async getZipData(zip: string): Promise<Array<ZipData> | undefined> {
    let response: Array<ZipData> | undefined;
    this.showLoading = true;
    try {
      if (this.endPoint) {
        response = await zipLookup(this.endPoint, sanitize(zip));
      }
      this.zipApiFailure = false;
      this.planApiFailure = false;
      if (this.isPlanDoc) {
        this.planDocZipApiFailure[this.activeTabIndex] = false;
        this.planDocPlanApiFailure[this.activeTabIndex] = false;
      }
      this.showLoading = false;
      if (response && response.length <= 0) {
        this.zipInputSuccess = false;
      } else {
        this.zipInputSuccess = true;
      }
    } catch (error) {
      response = undefined;
      this.zipApiFailure = true;
      if (this.isPlanDoc) {
        this.planDocZipApiFailure[this.activeTabIndex] = true;
      }
      this.showLoading = false;
      this.zipInputSuccess = false;
    }
    return response;
  }

  async getPlanResultsData(zip: string, fips: string): Promise<void> {
    let response: PlanResultsData | undefined;
    this.showContractLoading = true;
    try {
      if (this.endPoint) {
        response = await planResults(this.endPoint, sanitize(zip), fips, this.activeYear);
      } else response = undefined;
      if (response) {
        this.planResultsData = response?.MedicarePlans.sort((a: PlanObject, b: PlanObject) =>
          a.ContractID > b.ContractID ? 1 : b.ContractID > a.ContractID ? -1 : a.PlanID > b.PlanID ? 1 : -1,
        );
        //dual year
        this.planDocResultsData[this.activeTabIndex] = this.planResultsData;
        this.showContractLoading = false;
      }
      this.planApiFailure = false;
      this.planDocPlanApiFailure[this.activeTabIndex] = false;
    } catch (error) {
      this.planApiFailure = true;
      if (this.isPlanDoc) {
        this.planDocPlanApiFailure[this.activeTabIndex] = true;
      }
      this.planDocResultsData[this.activeTabIndex] = [];
      this.planResultsData = [];
      this.showContractLoading = false;
    }
  }

  buildLinkParams(planId: string | undefined) {
    let params;
    if (this.isPlanDoc && planId && planId !== 'unknown') {
      params = `zip=${this.zip}&fip=${this.filteredZipData?.CountyFIPS}&PlanID=${planId?.replace(/[-]/g, '|')}`;
    } else {
      params = `zip=${this.zip}&fip=${this.filteredZipData?.CountyFIPS}`;
    }
    if (!this.isPlanDoc) {
      params += `&PlanType=${this.planType}`;
    } else {
      params += `&PlanYear=${this.activeYear}`;
      if (location.pathname.includes('es-us')) {
        params += '&PlanLng=es-us';
      }
    }

    if (this.pid) {
      params += `&PID=${this.pid}`;
    }
    return params;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getCurrentParams() {
    const searchParams = window.location.search.split('?');
    const pathname = window.location.pathname;
    let currentParams = '';
    let hasCid,
      hasCampaignId = false;

    if (searchParams.length > 1) {
      currentParams = `&${searchParams[1]}`;
    }
    if (Cookies.get('medSuppCampaign-cipublic') || searchParams.find((x) => x.includes('campaign_ID')))
      hasCampaignId = true;
    if (Cookies.get('campaign-cid-cipublic') || searchParams.find((x) => x.includes('cid'))) hasCid = true;

    if (
      hasCampaignId &&
      Cookies.get('medSuppCampaign-cipublic') !== 'CSBORG' &&
      (!currentParams || currentParams.match(/(campaign_ID=CSBORG)/gm))
    ) {
      const campaignLink = updateDynamicLinks('plan-finder', this.campaignsObj);
      const campaignParams = campaignLink.split('?');
      currentParams = `&${campaignParams[1]}`;
    }

    if (this.planType === 'Supp') {
      if (pathname.match(/(medicare)/gm)) {
        if (!hasCid) currentParams += `&cid=s030`;
      } else {
        if (!hasCid) currentParams += `&cid=s037`;
        if (!hasCampaignId) currentParams += `&campaign_ID=&ANCORG`;
        currentParams += `&ee=dtcsupphealth`;
      }
    }
    return currentParams;
  }

  buildDeeplink = (planId: string | undefined = undefined) => {
    let plansCignaUrl = `${this.igseEndPoint}?`;
    let urlHash;
    if (this.isPlanDoc && planId) {
      urlHash = planId !== 'unknown' ? '#plandocuments' : '';
    } else {
      urlHash = this.planType === 'MAPD' ? '#ma' : this.planType === 'Supp' ? '#supplemental' : '#pdp';
    }
    const params = this.buildLinkParams(planId);
    plansCignaUrl = plansCignaUrl.concat(params);
    if (window.location.href.indexOf('/es-us/') > -1) plansCignaUrl = plansCignaUrl.concat('&lng=es-us');
    const currentParams = this.getCurrentParams();
    if (currentParams) {
      plansCignaUrl = plansCignaUrl.concat(currentParams);
    }
    plansCignaUrl = plansCignaUrl.concat(urlHash);
    return plansCignaUrl;
  };

  async handleZipSubmissionOrCounty(canSubmit: boolean) {
    this.showCountyView = false;
    if (this.zipLookupData && this.zipLookupData.length > 0) {
      if (this.zipLookupData.length === 1 && canSubmit) {
        this.filteredZipData = this.zipLookupData[0];
        window.location.hash = `zip=${this.zip}`;
        const redirectUrl = this.buildDeeplink();
        window.location.assign(redirectUrl);
      } else if (this.zipLookupData.length > 1) {
        this.initialView = false;
        this.showCountyView = true;
        if (this.variant === 'basic') {
          this.shadowRoot?.querySelector('#county-modal')?.setAttribute('open', 'true');
        }
      }
    }
  }

  async handleZipSubmissionOrCounty2() {
    this.showCountyView = false;
    if (this.zipLookupData && this.zipLookupData.length > 0) {
      if (this.zipLookupData.length === 1) {
        this.filteredZipData = this.zipLookupData[0];
        window.location.hash = `zip=${this.zip}`;
        const redirectUrl = this.buildDeeplink();
        window.location.assign(redirectUrl);
      } else if (this.zipLookupData.length > 1) {
        this.initialView = false;
        this.showCountyView = true;
        if (this.variant === 'basic') {
          this.shadowRoot?.querySelector('#county-modal')?.setAttribute('open', 'true');
          this.attachFormSubmit();
        }
      }
    }
  }

  async handleZipSubmissionPlanDoc() {
    this.showCountyView = false;
    this.showContractSelect = false;
    if (this.zipLookupData && this.zipLookupData.length > 0) {
      this.initialView = false;
      this.showCountyView = true;
      await this.updateComplete;
      const activeTabSection = this.shadowRoot?.querySelector('leaf-tab-section[active]');
      await (activeTabSection?.querySelector('leaf-select[name="county-select"]') as this)?.updateComplete;
      this.county = activeTabSection?.querySelector('leaf-select[name="county-select"]') || undefined;
      this.planDocCounties[this.activeTabIndex] = this.county?.value || '';
      if (this.zipLookupData.length === 1) {
        this.filteredZipData = this.zipLookupData[0];
        window.location.hash = `zip=${this.zip}`;
        this.showContractSelect = true;
      }
    }
  }

  handleZipChange = async (zip: string | undefined) => {
    if (zip) {
      const sanitizedZip = sanitize(zip);
      const zipResponse = await this.getZipData(sanitizedZip);
      const activeTab = this.shadowRoot?.querySelector('leaf-tab-item[active]');
      this.activeTabIndex = parseInt(activeTab?.getAttribute('data-index') || '0');
      if (zipResponse) {
        this.zipLookupData = zipResponse;
        this.planDocZipLookupData[this.activeTabIndex] = zipResponse;
        this.zip = sanitizedZip;
        this.planResultsData = [];
        if (this.zipLookupData && this.zipLookupData.length > 1) {
          this.zipInputChanged = false;
        } else if (this.zipLookupData && this.zipLookupData.length === 1 && this.isPlanDoc) {
          this.planDocZipArray[this.activeTabIndex] = this.zip;
          this.getContracts(this.zipLookupData[0].CountyName);
        }
      }
    }
  };

  handleZipInputChange = (event: InputEvent) => {
    const zipLeafInput = event.currentTarget as unknown as LeafInput;
    zipLeafInput.error = '';
    this.zipInputSuccess = false;
    zipLeafInput.internals.checkValidity();
    if ((this.variant === 'wizard' || this.isPlanDoc) && this.showCountyView) {
      this.zipInputChanged = true;
      this.showCountyView = false;
      this.showContractSelect = false;
    }
  };

  handleZipForm = (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const zipLeafInput = form.querySelector('#zip-input-id') as unknown as LeafInput;
    const zipInput = form.querySelector('#zip-input-id input') as HTMLInputElement;
    this.showLoading = true;
    this.handleZipChange(zipInput?.value).then(() => {
      if (this.zipApiFailure) {
        zipLeafInput.error = this.planFinderData?.validationMessages.apiFailureMessage;
      } else if (this.zipInputSuccess === false) {
        zipLeafInput.error = this.planFinderData?.validationMessages.zipCodeNotFound;
      } else {
        // this.handleZipSubmissionOrCounty(true);
        this.handleZipSubmissionOrCounty2();
      }
    });
  };

  handleWizardForm = (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const zipLeafInput = form.querySelector('#zip-input-id') as unknown as LeafInput;
    const zipInput = form.querySelector('#zip-input-id input') as HTMLInputElement;
    if (!this.showCountyView) {
      this.zipInputChanged = false;
      this.showLoading = true;
      this.handleZipChange(zipInput?.value).then(() => {
        if (this.zipApiFailure) {
          zipLeafInput.error = this.planFinderData?.validationMessages.apiFailureMessage;
        } else if (this.zipInputSuccess === false) {
          zipLeafInput.error = this.planFinderData?.validationMessages.zipCodeNotFound;
        } else {
          this.handleZipSubmissionOrCounty(true);
        }
      });
    } else {
      const countySelect: HTMLSelectElement | null = form.querySelector('#county-select-id select');
      const locationHash = `zip=${this.zip}`;
      const countyVal = countySelect ? sanitize(countySelect.value) : undefined;
      this.filteredZipData = this.zipLookupData.find((lookup) => lookup.CountyName === countyVal);
      if (this.filteredZipData?.CountyFIPS) {
        window.location.hash = locationHash;
        const redirectUrl = this.buildDeeplink();
        window.location.assign(redirectUrl);
      }
    }
  };

  handlePlanDocumentForm = (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const planId = formData.get('contract-select');
    const zipLeafInput = form.querySelector('#zip-input-id') as unknown as LeafInput;
    const zipInput = form.querySelector('#zip-input-id input') as HTMLInputElement;
    if (!this.showCountyView) {
      this.zipInputChanged = false;
      this.showLoading = true;
      this.handleZipChange(zipInput?.value).then(() => {
        if (this.zipApiFailure) {
          zipLeafInput.error = this.planFinderData?.validationMessages.apiFailureMessage;
        } else if (this.zipInputSuccess === false) {
          zipLeafInput.error = this.planFinderData?.validationMessages.zipCodeNotFound;
        } else {
          this.handleZipSubmissionPlanDoc();
        }
      });
    } else {
      const countySelect: HTMLSelectElement | null = form.querySelector('#county-select-id select');
      const locationHash = `zip=${this.zip}`;
      const countyVal = countySelect ? sanitize(countySelect.value) : undefined;
      this.filteredZipData = this.zipLookupData.find((lookup) => lookup.CountyName === countyVal);
      if (this.filteredZipData?.CountyFIPS && planId) {
        window.location.hash = locationHash;
        const redirectUrl = this.buildDeeplink(sanitize(String(planId)));
        window.location.assign(redirectUrl);
      }
    }
  };

  updatePlanResultsData() {
    if (this.zip && this.filteredZipData?.CountyFIPS) {
      this.getPlanResultsData(this.zip, this.filteredZipData?.CountyFIPS).then(async () => {
        this.showContractSelect = true;
        this.showContractLoading = false;
      });
    }
  }

  getContracts(county: string) {
    this.showContractSelect = false;
    this.showContractLoading = false;
    this.filteredZipData = this.zipLookupData.find((lookup) => lookup.CountyName === county);
    if (this.zipLookupData.length === 1) {
      this.filteredZipData = this.zipLookupData[0];
    }
    this.updatePlanResultsData();
  }

  handleCountyChange = (event: InputEvent) => {
    const countyVal = sanitize((event?.target as HTMLSelectElement).value);
    this.getContracts(countyVal);
  };

  renderZipField(label: string | undefined = undefined): TemplateResult {
    return html`
      <div>
        ${label ? html`<leaf-label for="zip-input-id">${ifDefined(label)}</leaf-label>` : ''}
        <leaf-input
          name="zip-input"
          id="zip-input-id"
          type="tel"
          pattern="^\\d{5}?$"
          required
          @input="${this.handleZipInputChange}"
          ?success-opt-in=${this.zipInputSuccess}
          maxlength="5"
        >
        </leaf-input>
      </div>
    `;
  }

  renderBasicCountyForm = () => {
    const renderHtml = `
    <p>${this.planFinderData?.modal.selectCountyLeadIn}</p>
    <form action="/" name="county-form">
      <leaf-label for="county-select-id">${this.planFinderData?.misc.countiesInText} ${this.zip}</leaf-label>
      <leaf-select id="county-select-id" name="county-select" required>
        <leaf-select-option value="" disabled selected>
        ${this.planFinderData?.misc.countyDefaultValue}</leaf-select-option>
        ${this.zipLookupData?.map(
          (data, index) =>
            `<leaf-select-option value="${data.CountyName}" ?selected=${
              this.zipLookupData.length === 1 && index === 0
            }>${data.CountyName}</leaf-select-option>`,
        )}
        ${this.variant === 'wizard' ? `<span slot="note">${this.planFinderData?.modal.countyHintText}</span>` : ''}
      </leaf-select>
      <leaf-button type="submit">${this.planFinderData?.modal.continuePlanBtn}</leaf-button>
    </form>`;
    return html`${unsafeHTML(renderHtml)}`;
  };

  renderCountyForm = () => {
    const label = this.isPlanDoc
      ? `${this.planFinderData?.misc.countyFieldLabel}`
      : `${this.planFinderData?.misc.countiesInText} ${this.zip}`;
    return html`
      <leaf-label for="county-select-id">${label}</leaf-label>
      <leaf-select id="county-select-id" name="county-select" required @change=${this.handleCountyChange}>
        <leaf-select-option value="" disabled ?selected=${this.zipLookupData.length !== 1}
          >${this.planFinderData?.misc.countyDefaultValue}</leaf-select-option
        >
        ${this.zipLookupData?.map(
          (data, index) =>
            html`<leaf-select-option
              value="${data.CountyName}"
              ?selected=${this.zipLookupData.length === 1 && index === 0}
              >${data.CountyName}</leaf-select-option
            >`,
        )}
        ${this.variant === 'wizard' ? `<span slot="note">${this.planFinderData?.modal.countyHintText}</span>` : ''}
      </leaf-select>
    `;
  };

  renderContractSelect = () => {
    return html`
      <leaf-label for="contract-select-id">${this.planFinderData?.misc.contractFieldLabel}</leaf-label>
      <leaf-select id="contract-select-id" name="contract-select" required>
        <leaf-select-option value="" disabled selected="true"
          >${this.planFinderData?.misc.contractDefaultValue}</leaf-select-option
        >
        ${this.planResultsData?.map(
          (plan: PlanObject) =>
            html`<leaf-select-option value="${plan.ID}"
              >${plan.PlanType === 1
                ? `${plan.ContractID}-${plan.PlanID}`
                : `${plan.ContractID}-${plan.PlanID}-${plan.SegmentID}`}</leaf-select-option
            >`,
        )}
        ${this.planResultsData?.length > 0
          ? html`<leaf-select-option value="unknown">${this.planFinderData?.misc.contractUnknown}</leaf-select-option>`
          : ''}
      </leaf-select>
    `;
  };

  renderCountyModal(heading: string): TemplateResult {
    return html`
      <leaf-modal id="county-modal" size="md">
        <h3 slot="heading">${heading}</h3>
        ${this.renderBasicCountyForm()}
      </leaf-modal>
    `;
  }

  renderZipForm(): TemplateResult {
    return html`<form name="zip-form" action="/" @submit=${this.handleZipForm}>
      <leaf-inputgroup>
        ${this.planFinderData?.misc.zipFieldLabel
          ? html`<leaf-label for="zip-input-id">${this.planFinderData?.misc.zipFieldLabel}</leaf-label>`
          : nothing}
        <leaf-input
          type="tel"
          id="zip-input-id"
          name="zip-input"
          pattern="^\\d{5}?$"
          @input="${this.handleZipInputChange}"
          ?success-opt-in=${this.zipInputSuccess}
          required
          maxlength="5"
        >
        </leaf-input>
        <leaf-button type="submit" ?loading=${this.showLoading}>
          ${this.planFinderData?.modal.shopForAPlanBtn}
        </leaf-button>
      </leaf-inputgroup>
    </form>`;
  }

  renderBasicPlanFinder(): TemplateResult {
    const heading = this.planFinderData?.modal.selectCountyHeader || '';
    return html`
      <div class="basic-zip-fields">${this.renderZipForm()}</div>
      ${this.renderCountyModal(heading)}
    `;
  }

  renderWizardPlanFinder() {
    return html` <form name="wizard-form" action="/" @submit=${this.handleWizardForm}>
      ${this.renderZipField(this.planFinderData?.misc.connectFormZipLabel)}
      ${!this.zipInputChanged && this.showCountyView ? html` ${this.renderCountyForm()}` : ''}
      <leaf-button type="submit">${this.planFinderData?.modal.shopForAPlanBtn}</leaf-button>
      <leaf-spinner size="small" ?hidden=${!this.showLoading}>${this.planFinderData?.misc.loading}</leaf-spinner>
    </form>`;
  }

  async toggleTabs() {
    const activeTab = this.shadowRoot?.querySelector('leaf-tab-item[active]');
    const selectedYear = activeTab?.getAttribute('label');
    this.activeTabIndex = parseInt(activeTab?.getAttribute('data-index') || '0');
    this.activeYear = parseInt(selectedYear || '');
    const activeTabSection = this.shadowRoot?.querySelector('leaf-tab-section[active]');
    this.county = activeTabSection?.querySelector('leaf-select[name="county-select"]') || undefined;
    this.contract = activeTabSection?.querySelector('leaf-select[name="contract-select"]') || undefined;
    this.zipApiFailure = this.planDocZipApiFailure[this.activeTabIndex];
    this.planApiFailure = this.planDocPlanApiFailure[this.activeTabIndex];
    this.zip = this.planDocZipArray[this.activeTabIndex];
    this.zipLookupData = this.planDocZipLookupData[this.activeTabIndex];
    this.showCountyView = this.planDocCountyView[this.activeTabIndex];
    await this.county?.requestUpdate();
    await this.contract?.requestUpdate();
    await this.updateComplete;
    this.zipInputChanged = !this.zip ? true : false;
    const selectedCounty = this.planDocCounties[this.activeTabIndex];
    const selectedContract = this.planDocContracts[this.activeTabIndex];
    if (selectedCounty) {
      this.showCountyView = true;
      (this.county?.querySelector('#county-select-id-leaf') as LeafSelect).value = selectedCounty;
      this.getContracts(selectedCounty);
    }
    if (selectedContract && this.contract) {
      this.contract.value = selectedContract;
    }
  }

  renderPlanYears() {
    if (this.planYears.length > 1) {
      this.setFinderProperties();
      const tabList: Array<TemplateResult> = [];
      const tabContent: Array<TemplateResult> = [];
      this.planYears.map((year, index) => {
        tabList.push(
          html`<leaf-tab-item
            scroll-target
            size="default"
            aria-selected=${index === 0 ? true : false}
            ?active=${index === 0 ? true : false}
            slot="tab"
            label="${year}"
            @click=${this.toggleTabs}
            data-index="${index}"
            >${year}</leaf-tab-item
          >`,
        );
        tabContent.push(
          html`<leaf-tab-section slot="tab-section" ?active=${index === 0 ? true : false} label="${year}">
            ${this.renderPlanDocumentFinder()}
          </leaf-tab-section>`,
        );
      });
      return html`<leaf-tab mobile-variant="scroll" tab-align="left">${tabList}${tabContent}</leaf-tab>`;
    }
    return html`${this.renderPlanDocumentFinder()}`;
  }

  renderPlanDocumentFinder() {
    return html` <form name="plan-doc-form" action="/" @submit=${this.handlePlanDocumentForm}>
      ${this.renderZipField(this.planFinderData?.misc.zipLabelText)}
      ${!this.zipInputChanged && this.showCountyView ? html` ${this.renderCountyForm()}` : ''}
      ${!this.zipInputChanged && this.showCountyView && this.showContractSelect && this.planResultsData.length > 0
        ? html` ${this.renderContractSelect()}`
        : ''}
      <leaf-button type="submit">${this.planFinderData?.misc.searchBtn}</leaf-button>
      <leaf-spinner size="small" ?hidden=${!this.showLoading}>${this.planFinderData?.misc.loading}</leaf-spinner>
      <leaf-spinner size="small" ?hidden=${!this.showContractLoading}
        >${this.planFinderData?.misc.loading}</leaf-spinner
      >
    </form>`;
  }

  override render() {
    const componentClassName = {
      'chcapp-medicare-plan-finder': true,
      'chcapp-medicare-plan-finder---initial': this.initialView,
    };
    const loadingOrError = this.renderLoadingOrError();
    if (loadingOrError) {
      return loadingOrError;
    }
    return html` <div class=${classMap(componentClassName)}>
      <slot></slot>
      ${this.variant === 'basic'
        ? this.renderBasicPlanFinder()
        : this.variant === 'wizard'
          ? this.renderWizardPlanFinder()
          : this.variant === 'plan-documents'
            ? this.renderPlanYears()
            : ''}
    </div>`;
  }
}
